import { Component, OnInit, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { FieldTestService } from '../../../../../core/services/field-test.service';
import { FieldTest } from '../../../../../core/models/field-test.model';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-field-test-details',
  standalone: true,
  imports: [CommonModule, RouterLink, ConfirmModalComponent],
  templateUrl: './field-test-details.component.html',
  styleUrls: ['./field-test-details.component.scss']
})
export class FieldTestDetailsComponent implements OnInit {
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fieldTestService = inject(FieldTestService);

  // State
  isLoading = signal(true);
  errorMessage = signal<string | null>(null);
  testId = signal<string | null>(null);
  test = signal<FieldTest | null>(null);

  // Computed stats
  readonly passedCount = computed(() => {
    const t = this.test();
    if (!t) return 0;
    return t.checklist.filter(item => item.passed).length;
  });

  readonly failedCount = computed(() => {
    const t = this.test();
    if (!t) return 0;
    return t.checklist.filter(item => !item.passed).length;
  });

  readonly totalItems = computed(() => {
    const t = this.test();
    return t ? t.checklist.length : 0;
  });

  readonly passPercentage = computed(() => {
    const total = this.totalItems();
    if (total === 0) return 0;
    return Math.round((this.passedCount() / total) * 100);
  });

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.testId.set(id);
      this.loadTest(id);
    } else {
      this.errorMessage.set('No test ID provided');
      this.isLoading.set(false);
    }
  }

  private async loadTest(id: string): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const found = await this.fieldTestService.fetchFieldTestById(id);
      if (found) {
        this.test.set(found);
      } else {
        this.errorMessage.set('Field test not found');
      }
    } catch (error) {
      console.error('Error loading field test:', error);
      this.errorMessage.set('Failed to load field test');
    } finally {
      this.isLoading.set(false);
    }
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/logbook/testing/field']);
  }

  editTest(): void {
    const id = this.testId();
    if (id) {
      this.router.navigate(['/logbook/testing/field', id, 'edit']);
    }
  }

  // Delete functionality
  confirmDelete(): void {
    const t = this.test();
    if (t) {
      this.confirmModal.open({
        title: 'Delete Field Test',
        message: `Are you sure you want to delete "${t.title}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonClass: 'btn-danger',
        icon: 'bi-trash'
      });
    }
  }

  async onDeleteConfirmed(): Promise<void> {
    const id = this.testId();
    if (id) {
      const success = await this.fieldTestService.deleteFieldTest(id);
      if (success) {
        this.router.navigate(['/logbook/testing/field']);
      }
    }
  }

  onDeleteCancelled(): void {
    // No action needed
  }

  // Helper methods
  getStatusClass(status: string): string {
    switch (status) {
      case 'passed': return 'bg-success';
      case 'failed': return 'bg-danger';
      case 'pending': return 'bg-warning';
      default: return 'bg-secondary';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'passed': return 'bi-check-circle-fill';
      case 'failed': return 'bi-x-circle-fill';
      case 'pending': return 'bi-clock-fill';
      default: return 'bi-question-circle';
    }
  }

  getTestTypeIcon(type: string): string {
    switch (type) {
      case 'pre-flight': return 'icon-drone';
      case 'post-flight': return 'icon-drone-fill';
      case 'equipment': return 'bi bi-tools';
      case 'calibration': return 'bi bi-speedometer2';
      default: return 'bi bi-clipboard-check';
    }
  }

  getTestTypeLabel(type: string): string {
    switch (type) {
      case 'pre-flight': return 'Pre-Flight Check';
      case 'post-flight': return 'Post-Flight Inspection';
      case 'equipment': return 'Equipment Test';
      case 'calibration': return 'Calibration';
      default: return type;
    }
  }

  formatDate(date: string): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}
